import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Announcements from '../components/Announcements';
import Slideshow from '../components/Slideshow';
import OrgChartDisplay from '../components/OrgChartDisplay';
import './Home.css';

function Home() {
  const { currentUser } = useAuth();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-container">
      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-overlay"></div>
        <div className="banner-content">
          <div className="banner-text-content">
            <h1>NSTP Management System</h1>
            <p className="hero-subtitle">Empowering Youth for Nation Building</p>
            <p>The National Service Training Program enhances civic consciousness and defense preparedness by developing skills of service and patriotism.</p>
            <div className="banner-buttons">
              {!currentUser ? (
                <>
                  <Link to="/register" className="btn btn-register">
                    <span>Register Now</span>
                    <i className="bi bi-arrow-right"></i>
                  </Link>
                  <Link to="/login" className="btn btn-login">
                    <i className="bi bi-box-arrow-in-right"></i>
                    <span>Login</span>
                  </Link>
                </>
              ) : (
                <Link to="/dashboard" className="btn btn-dashboard">
                  <i className="bi bi-speedometer2"></i>
                  <span>Go to Dashboard</span>
                </Link>
              )}
            </div>
          </div>
          <div className="banner-stats">
            <div className="stat-card">
              <div className="stat-number">1000+</div>
              <div className="stat-label">Students Enrolled</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">3</div>
              <div className="stat-label">NSTP Courses</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">50+</div>
              <div className="stat-label">Faculty Members</div>
            </div>
          </div>
        </div>
      </div>

      {/* Slideshow Section */}
      <div className="slideshow-section">
        <div className="section-header">
          <h2>Featured Highlights</h2>
          <p className="section-subtitle">Latest activities and events from our NSTP program</p>
        </div>
        <Slideshow />
      </div>

      {/* Key Features Section */}
      <div className="features-section">
        <div className="section-header">
          <h2>Key Features</h2>
          <p className="section-subtitle">Discover what our NSTP Management System offers</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <i className="bi bi-mortarboard-fill"></i>
            </div>
            <h3>NSTP Enrollment</h3>
            <p>Easy and streamlined enrollment process for students joining the NSTP program</p>
            <Link to="/register" className="feature-link">Enroll Now <i className="bi bi-arrow-right"></i></Link>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="bi bi-laptop-fill"></i>
            </div>
            <h3>Online Learning</h3>
            <p>Access to online modules and resources for continuous learning and development</p>
            <Link to="/login" className="feature-link">Access Resources <i className="bi bi-arrow-right"></i></Link>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="bi bi-bell-fill"></i>
            </div>
            <h3>Real-time Updates</h3>
            <p>Get notified about important announcements, schedules, and program activities</p>
            <a href="#announcements" className="feature-link">View Announcements <i className="bi bi-arrow-right"></i></a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="bi bi-file-earmark-text-fill"></i>
            </div>
            <h3>Digital Records</h3>
            <p>Secure digital storage of attendance, grades, certificates, and other documents</p>
            <Link to="/login" className="feature-link">Access Records <i className="bi bi-arrow-right"></i></Link>
          </div>
        </div>
      </div>

      {/* Latest Announcements */}
      <div id="announcements" className="announcements-section">
        <div className="section-header">
          <h2>Latest Announcements</h2>
          <p className="section-subtitle">Stay updated with important information and events</p>
        </div>
        <div className="announcements-list">
          <Announcements limit={3} showTitle={false} />
        </div>
      </div>

      {/* NSTP Programs */}
      <div className="programs-section">
        <div className="section-header">
          <h2>NSTP Programs</h2>
          <p className="section-subtitle">Choose the program that fits your interests and goals</p>
        </div>
        <p className="programs-description">
          The National Service Training Program (NSTP) enhances civic consciousness and defense preparedness in youth through targeted skills development in service and patriotism.
        </p>
        
        <div className="programs-grid">
          <div className="program-card">
            <div className="program-icon">
              <i className="bi bi-shield-fill"></i>
            </div>
            <h3>ROTC</h3>
            <p>Reserve Officers' Training Corps - Designed to provide military education and training for students to mobilize them for national defense preparedness.</p>
            <ul className="program-features">
              <li><i className="bi bi-check-circle-fill"></i> Basic military training</li>
              <li><i className="bi bi-check-circle-fill"></i> Leadership development</li>
              <li><i className="bi bi-check-circle-fill"></i> Physical fitness training</li>
              <li><i className="bi bi-check-circle-fill"></i> Military values and traditions</li>
            </ul>
            <Link to="/register" className="program-btn">Enroll in ROTC</Link>
          </div>
          
          <div className="program-card">
            <div className="program-icon">
              <i className="bi bi-people-fill"></i>
            </div>
            <h3>CWTS</h3>
            <p>Civic Welfare Training Service - Designed to improve health, education, environment, entrepreneurship, safety, and morals of the citizenry.</p>
            <ul className="program-features">
              <li><i className="bi bi-check-circle-fill"></i> Environmental conservation</li>
              <li><i className="bi bi-check-circle-fill"></i> Community service projects</li>
              <li><i className="bi bi-check-circle-fill"></i> Health and welfare activities</li>
              <li><i className="bi bi-check-circle-fill"></i> Disaster preparedness training</li>
            </ul>
            <Link to="/register" className="program-btn">Enroll in CWTS</Link>
          </div>
          
          <div className="program-card">
            <div className="program-icon">
              <i className="bi bi-book-fill"></i>
            </div>
            <h3>LTS</h3>
            <p>Literacy Training Service - Designed to train students to teach literacy and numeracy skills to school children, out-of-school youth, and other sectors of society.</p>
            <ul className="program-features">
              <li><i className="bi bi-check-circle-fill"></i> Teaching methodologies</li>
              <li><i className="bi bi-check-circle-fill"></i> Educational material development</li>
              <li><i className="bi bi-check-circle-fill"></i> Literacy and numeracy skills</li>
              <li><i className="bi bi-check-circle-fill"></i> Community education programs</li>
            </ul>
            <Link to="/register" className="program-btn">Enroll in LTS</Link>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="testimonials-section">
        <div className="section-header">
          <h2>What Our Students Say</h2>
          <p className="section-subtitle">Experiences from NSTP participants</p>
        </div>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="testimonial-quote">
              <i className="bi bi-quote"></i>
            </div>
            <p>NSTP has taught me valuable life skills and the importance of service to our community. It was truly a transformative experience.</p>
            <div className="testimonial-author">
              <div className="author-avatar">
                <i className="bi bi-person-circle"></i>
              </div>
              <div className="author-info">
                <h4>Maria Santos</h4>
                <p>CWTS Graduate, 2022</p>
              </div>
            </div>
          </div>
          
          <div className="testimonial-card">
            <div className="testimonial-quote">
              <i className="bi bi-quote"></i>
            </div>
            <p>The leadership training I received during ROTC has been invaluable in my career. I learned discipline, teamwork, and resilience.</p>
            <div className="testimonial-author">
              <div className="author-avatar">
                <i className="bi bi-person-circle"></i>
              </div>
              <div className="author-info">
                <h4>Juan Dela Cruz</h4>
                <p>ROTC Graduate, 2021</p>
              </div>
            </div>
          </div>
          
          <div className="testimonial-card">
            <div className="testimonial-quote">
              <i className="bi bi-quote"></i>
            </div>
            <p>Teaching literacy through LTS gave me a deeper appreciation for education and how it can transform lives in underserved communities.</p>
            <div className="testimonial-author">
              <div className="author-avatar">
                <i className="bi bi-person-circle"></i>
              </div>
              <div className="author-info">
                <h4>Anna Reyes</h4>
                <p>LTS Graduate, 2022</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organizational Chart */}
      <div className="organization-section">
        <div className="section-header">
          <h2>Our Structure</h2>
          <p className="section-subtitle">Meet our leadership team and understand our organizational structure</p>
        </div>
        <div className="org-chart-container">
          <OrgChartDisplay />
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="cta-overlay"></div>
        <div className="cta-content">
          <div className="cta-icon">
            <i className="bi bi-people-fill"></i>
          </div>
          <h2>Ready to Begin Your NSTP Journey?</h2>
          <p>Register now to access your personalized dashboard, view announcements, track your progress, and connect with instructors and fellow students.</p>
          <div className="cta-buttons">
            {!currentUser ? (
              <>
                <Link to="/register" className="btn btn-register-alt">START YOUR JOURNEY</Link>
                <Link to="/programs" className="btn btn-learn-more">LEARN MORE</Link>
              </>
            ) : (
              <Link to="/dashboard" className="btn btn-dashboard-alt">GO TO DASHBOARD</Link>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-section about">
            <h3>NSTP Management System</h3>
            <p>A comprehensive platform for managing all aspects of the National Service Training Program implementation, student enrollment, training, and assessment.</p>
            <div className="social-links">
              <a href="#" className="social-icon"><i className="bi bi-facebook"></i></a>
              <a href="#" className="social-icon"><i className="bi bi-twitter"></i></a>
              <a href="#" className="social-icon"><i className="bi bi-instagram"></i></a>
              <a href="#" className="social-icon"><i className="bi bi-youtube"></i></a>
            </div>
          </div>
          
          <div className="footer-section links">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/login"><i className="bi bi-chevron-right"></i> Login</Link></li>
              <li><Link to="/register"><i className="bi bi-chevron-right"></i> Register</Link></li>
              <li><Link to="/programs"><i className="bi bi-chevron-right"></i> Programs</Link></li>
              <li><Link to="/faq"><i className="bi bi-chevron-right"></i> FAQs</Link></li>
              <li><Link to="/contact"><i className="bi bi-chevron-right"></i> Contact Us</Link></li>
            </ul>
          </div>
          
          <div className="footer-section contact">
            <h4>Contact Information</h4>
            <div className="contact-details">
              <p><i className="bi bi-geo-alt-fill"></i> 123 University Avenue, Manila, Philippines</p>
              <p><i className="bi bi-envelope-fill"></i> nstp@example.edu.ph</p>
              <p><i className="bi bi-telephone-fill"></i> +63 (2) 123-4567</p>
              <p><i className="bi bi-clock-fill"></i> Mon - Fri: 8:00 AM to 5:00 PM</p>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>&copy; {new Date().getFullYear()} NSTP Management System. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link> | 
            <Link to="/terms"> Terms of Service</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Home;